import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, BellIcon, QrCodeIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores';
import { usePermissions } from '../../hooks/usePermissions';
import { useAlertsStore } from '../../stores/useAlertsStore';
import { useModuleVisibility, ALL_MODULES } from '../../hooks/useModuleVisibility';
import { ModuleManagerPanel, AddModuleButton } from './ModuleManager';
import { isSuperAdminEmail } from '../../constants/superAdmin';

interface SidebarProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onVentaRapida?: () => void;
}

// Default navigation items (conditionally visible based on tipo_usuario)
// Note: Dashboard/CRM is now in the Módulos section, not here
const defaultNavigation: Array<{ name: string; href: string; icon: string; moduloId: string | null }> = [];

// Map of routes to module IDs for permission checking
/** Etiqueta en sidebar: alinear con mobile (ALL_MODULES); la BD puede tener nombres legacy. */
function sidebarLabelForModule(moduloId: string, nombreFromDb: string): string {
  const def = ALL_MODULES.find((m) => m.id === moduloId);
  return def?.label ?? nombreFromDb;
}

const routeToModuleId: Record<string, string> = {
  '/calendario': 'calendario',
  '/facturas': 'facturas',
  '/ventas/facturas': 'facturas',
  '/gastos': 'gastos',
  '/cierre': 'cierre_caja',
  '/productos': 'productos',
  '/ventas': 'ventas',
  '/pedidos': 'ventas',
  '/clientes': 'clientes',
  '/mensajes': 'mensajes',
  '/business-intelligence': 'business_intelligence',
  '/reportes': 'reportes',
  '/configuracion': 'configuracion',
  '/usuarios': 'usuarios',
  '/cobros': 'cobros',
};

/**
 * Orden del menú «Módulos» (después de Resumen Ejecutivo, Alertas, Pedidos).
 * Debe reflejar el flujo de trabajo acordado en producto.
 */
const SIDEBAR_MAIN_MODULE_ORDER: string[] = [
  'calendario',
  'mensajes',
  'productos',
  'cobros',
  'clientes',
  'ventas',
  'gastos',
  'cierre_caja',
];

function mainModuleOrderIndex(id: string): number {
  const i = SIDEBAR_MAIN_MODULE_ORDER.indexOf(id);
  return i === -1 ? 1000 : i;
}

/** Icono en sidebar (emoji); si no hay, se usa `modulo.icono` de la BD. */
const SIDEBAR_MODULE_ICON: Partial<Record<string, string>> = {
  calendario: '📅',
  mensajes: '💬',
  productos: '📦',
  cobros: '💳',
  clientes: '👥',
  ventas: '💰',
  gastos: '💸',
  cierre_caja: '📋',
};

export const Sidebar: React.FC<SidebarProps> = ({ open, setOpen, onVentaRapida }) => {
  const location = useLocation();
  const { usuario, currentEmpresa } = useAuthStore();
  const { visibilityMap, hiddenModules, toggleModule, isVisible } = useModuleVisibility();
  const [moduleManagerOpen, setModuleManagerOpen] = useState(false);
  const { availableModules, canAccess, canAccessDashboard, loading: permissionsLoading } = usePermissions(
    usuario?.id || null,
    currentEmpresa?.id || null
  );
  const { unreadCount, fetchAlerts } = useAlertsStore();
  
  // Fetch alerts on mount and when empresa changes
  useEffect(() => {
    if (currentEmpresa?.id) {
      fetchAlerts(currentEmpresa.id);
    }
  }, [currentEmpresa?.id, fetchAlerts]);

  // Collapsible sections state - when one opens, the other closes
  const [modulesCollapsed, setModulesCollapsed] = useState(false);
  const [biCollapsed, setBiCollapsed] = useState(true);
  const [configCollapsed, setConfigCollapsed] = useState(true);
  
  // Auto-expand sections based on current route
  React.useEffect(() => {
    // Check if we're in dashboard (CRM) route
    if (location.pathname.startsWith('/dashboard')) {
      setModulesCollapsed(false);
      setBiCollapsed(true);
      setConfigCollapsed(true);
      return;
    }

    // Reportes PDF (/reportes, /reportes/ventas, …): must run before isMainModuleRoute.
    // The RBAC module "reportes" uses ruta "/reportes", so /reportes/ventas would otherwise
    // match as a "main module" route and collapse Business Intelligence (where Reportes lives).
    if (location.pathname.startsWith('/reportes')) {
      setBiCollapsed(false);
      setModulesCollapsed(true);
      setConfigCollapsed(true);
      return;
    }

    if (location.pathname.startsWith('/cobros')) {
      setModulesCollapsed(false);
      setBiCollapsed(true);
      setConfigCollapsed(true);
      return;
    }
    
    // Check if we're in a main module route (not dashboard, not BI, not config)
    const isMainModuleRoute = availableModules.some(modulo => {
      if (modulo.id === 'business_intelligence' || modulo.id === 'configuracion' || modulo.id === 'usuarios') {
        return false;
      }
      // Avoid prefix clash: legacy modulos row reportes → /reportes (handled above).
      if (modulo.id === 'reportes') {
        return false;
      }
      if (modulo.id === 'facturas') {
        return false;
      }
      return location.pathname.startsWith(modulo.ruta);
    });
    
    if (isMainModuleRoute) {
      setModulesCollapsed(false);
      setBiCollapsed(true);
      setConfigCollapsed(true);
    } else if (location.pathname.startsWith('/business-intelligence')) {
      setBiCollapsed(false);
      setModulesCollapsed(true);
      setConfigCollapsed(true);
    } else if (
      location.pathname.startsWith('/configuracion') ||
      location.pathname.startsWith('/super-admin') ||
      location.pathname.startsWith('/colecciones')
    ) {
      setConfigCollapsed(false);
      setModulesCollapsed(true);
      setBiCollapsed(true);
    }
  }, [location.pathname, availableModules]);
  
  const toggleModules = () => {
    setModulesCollapsed(!modulesCollapsed);
    if (!modulesCollapsed) {
      // If collapsing Modules, do nothing
    } else {
      // If expanding Modules, collapse others
      setBiCollapsed(true);
      setConfigCollapsed(true);
    }
  };
  
  const toggleBI = () => {
    setBiCollapsed(!biCollapsed);
    if (!biCollapsed) {
      // If collapsing BI, do nothing
    } else {
      // If expanding BI, collapse others
      setModulesCollapsed(true);
      setConfigCollapsed(true);
    }
  };
  
  const toggleConfig = () => {
    setConfigCollapsed(!configCollapsed);
    if (!configCollapsed) {
      // If collapsing Config, do nothing
    } else {
      // If expanding Config, collapse others
      setModulesCollapsed(true);
      setBiCollapsed(true);
    }
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    // Listado de ventas (histórico) — ruta exacta (no /ventas/…)
    if (href === '/ventas') {
      return location.pathname === '/ventas';
    }
    if (href === '/productos') {
      return location.pathname === '/productos' || (location.pathname.startsWith('/productos/') && !location.pathname.startsWith('/productos/estudio'));
    }
    if (href === '/productos/estudio') {
      return location.pathname.startsWith('/productos/estudio');
    }
    // Support query-string based routes (e.g. /configuracion?tab=tienda-publica)
    if (href.includes('?')) {
      return `${location.pathname}${location.search}`.startsWith(href);
    }
    return location.pathname.startsWith(href);
  };

  // Check if a route should be visible based on permissions
  const isRouteVisible = (href: string): boolean => {
    // Dashboard: Only Admin and Contador can access (per empresa)
    if (href === '/dashboard') {
      return canAccessDashboard();
    }
    
    // Check if route has a module ID mapping
    const moduleId = routeToModuleId[href];
    if (!moduleId) return true; // If no mapping, show by default (backward compatibility)
    
    // Check permissions via RBAC
    return canAccess(moduleId);
  };

  return (
    <>
      {/* Overlay para móvil */}
      {open && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </div>
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:fixed flex flex-col
        ${open ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-blue-600">Clientelo</h1>
            </div>
          </div>
          
          {/* Botón cerrar para móvil */}
          <div className="lg:hidden">
            <button
              onClick={() => setOpen(false)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Cerrar menú</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Información del usuario */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {usuario?.nombre.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{usuario?.nombre}</p>
              <p className="text-xs text-gray-500">{usuario?.empresa}</p>
            </div>
          </div>

          {/* Edit modules: show/hide sidebar sections */}
          <div className="mt-3">
            <AddModuleButton
              hiddenCount={hiddenModules.length}
              onClick={() => setModuleManagerOpen(true)}
            />
            <p className="text-xs text-gray-500 mt-1.5 px-1">
              Mostrar u ocultar secciones del menú
            </p>
          </div>

          {onVentaRapida ? (
            <button
              type="button"
              onClick={() => {
                onVentaRapida();
                setOpen(false);
              }}
              className="mt-3 flex min-h-[44px] w-full items-center justify-center rounded-lg bg-[#1A56DB] px-3 py-2.5 text-sm font-semibold text-white hover:bg-[#1648c0] focus:outline-none focus:ring-2 focus:ring-[#1A56DB] focus:ring-offset-2"
            >
              + Nueva factura
            </button>
          ) : null}
        </div>

        {/* Scrollable navigation area */}
        <div className="flex-1 overflow-y-auto max-lg:[&_a]:min-h-[44px] max-lg:[&_a]:py-3">
          {/* Navegación */}
          <nav className="mt-6 px-3">
            <div className="space-y-1">
              {/* Default navigation (filtered by permissions) */}
              {defaultNavigation
                .filter(item => {
                  // Dashboard visibility is controlled by canAccessDashboard
                  if (item.href === '/dashboard') {
                    return canAccessDashboard();
                  }
                  // Other default items always visible (if any added in future)
                  return true;
                })
                .map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive(item.href)
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">{item.icon}</span>
                    {item.name}
                  </Link>
                ))}

              {/* Módulos Principales - Collapsible */}
              {permissionsLoading ? (
                <div className="px-3 py-2 text-sm text-gray-500">
                  Cargando permisos...
                </div>
              ) : availableModules.length === 0 ? (
                <div className="px-3 py-2 text-sm text-yellow-600">
                  ⚠️ No hay módulos disponibles. Verificando permisos...
                </div>
              ) : (
                <>
                  <div className="mt-8 px-3">
                    <button
                      onClick={toggleModules}
                      className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <span>Módulos</span>
                      {modulesCollapsed ? (
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    {!modulesCollapsed && (
                      <div className="mt-2 space-y-1">
                        {/* CRM (Dashboard) - First item if user has access and visible */}
                        {canAccessDashboard() && isVisible('dashboard') && (
                          <Link
                            key="crm"
                            to="/dashboard"
                            onClick={() => setOpen(false)}
                            className={`
                              group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                              ${isActive('/dashboard')
                                ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }
                            `}
                          >
                            <span className="mr-3 text-lg">📊</span>
                            Resumen Ejecutivo
                          </Link>
                        )}

                        {isVisible('alertas') && (
                          <Link
                            key="alertas"
                            to="/dashboard/alertas"
                            onClick={() => setOpen(false)}
                            className={`
                            group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                            ${isActive('/dashboard/alertas')
                              ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                            }
                          `}
                          >
                            <div className="flex items-center">
                              <BellIcon className="h-5 w-5 mr-3 text-gray-600" />
                              Alertas
                            </div>
                            {unreadCount > 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </Link>
                        )}

                        {canAccess('ventas') && isVisible('ventas') && (
                          <Link
                            key="pedidos-cola"
                            to="/pedidos"
                            onClick={() => setOpen(false)}
                            className={`
                              group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                              ${isActive('/pedidos')
                                ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }
                            `}
                          >
                            <span className="mr-3 text-lg">📋</span>
                            Pedidos
                          </Link>
                        )}

                        {availableModules
                          .filter((modulo) => {
                            if (modulo.id === 'pipeline' || modulo.id === 'facturas') return false;
                            if (
                              modulo.id === 'business_intelligence' ||
                              modulo.id === 'reportes' ||
                              modulo.id === 'configuracion' ||
                              modulo.id === 'usuarios'
                            ) {
                              return false;
                            }
                            if (!isVisible(modulo.id)) return false;
                            return isRouteVisible(modulo.ruta);
                          })
                          .sort((a, b) => mainModuleOrderIndex(a.id) - mainModuleOrderIndex(b.id))
                          .map((modulo) => {
                            const href = modulo.ruta;
                            const linkActive = isActive(href);
                            const icon = SIDEBAR_MODULE_ICON[modulo.id] ?? modulo.icono;
                            return (
                              <Link
                                key={modulo.id}
                                to={href}
                                onClick={() => setOpen(false)}
                                className={`
                                  group flex items-center justify-between px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                                  ${linkActive
                                    ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                  }
                                `}
                              >
                                <span className="flex items-center min-w-0">
                                  <span className="mr-3 text-lg flex-shrink-0">{icon}</span>
                  <span className="leading-tight break-words">
                    {sidebarLabelForModule(modulo.id, modulo.nombre)}
                  </span>
                                </span>
                              </Link>
                            );
                          })}
                        {canAccess('productos') && isVisible('centro_diseno') && (
                          <Link
                            key="centro-diseno"
                            to="/productos/estudio"
                            onClick={() => setOpen(false)}
                            className={`
                              group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                              ${isActive('/productos/estudio')
                                ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }
                            `}
                          >
                            <span className="mr-3 text-lg">🎨</span>
                            Centro de Diseño
                          </Link>
                        )}
                        {canAccess('configuracion') && isVisible('configuracion') && (
                          <Link
                            key="vitrina-virtual"
                            to="/configuracion?tab=vitrina"
                            onClick={() => setOpen(false)}
                            className={`
                              group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                              ${isActive('/configuracion') && location.search.includes('tab=vitrina')
                                ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                              }
                            `}
                          >
                            <span className="mr-3 text-lg">🧭</span>
                            Vitrina Virtual
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </nav>

          {/* Business Intelligence + Reportes PDF (no duplicar en Módulos) */}
          {((canAccess('business_intelligence') && isVisible('business_intelligence')) ||
            (canAccess('reportes') && isVisible('reportes'))) && (
            <div className="mt-8 px-3">
              <button
                onClick={toggleBI}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-md transition-colors"
              >
                <span>Business Intelligence</span>
                {biCollapsed ? (
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {!biCollapsed && (
                <div className="mt-2 space-y-1">
                  {canAccess('reportes') && isVisible('reportes') && (
                    <Link
                      to="/reportes"
                      onClick={() => setOpen(false)}
                      className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/reportes')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                    >
                      <span className="mr-3 text-lg">📄</span>
                      Reportes
                    </Link>
                  )}
                  {canAccess('business_intelligence') && isVisible('business_intelligence') && (
                    <>
                  <Link
                    to="/business-intelligence"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${location.pathname === '/business-intelligence' && !location.search.includes('modulo=')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📊</span>
                    Dashboard BI
                  </Link>
                  <Link
                    to="/business-intelligence?modulo=rentabilidad"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${location.pathname === '/business-intelligence' && location.search.includes('modulo=rentabilidad')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📊</span>
                    Análisis de Rentabilidad
                  </Link>
                  <Link
                    to="/business-intelligence?modulo=abc"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${location.pathname === '/business-intelligence' && location.search.includes('modulo=abc')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📦</span>
                    Análisis ABC
                  </Link>
                  <Link
                    to="/business-intelligence/churn"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/business-intelligence/churn')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🔮</span>
                    Análisis de Churn
                  </Link>
                  <Link
                    to="/business-intelligence?modulo=predictivo"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${location.pathname === '/business-intelligence' && location.search.includes('modulo=predictivo')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🔮</span>
                    Análisis Predictivo
                  </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sección de Configuración - Only show if user has access and visible */}
          {canAccess('configuracion') && isVisible('configuracion') && (
            <div className="mt-8 px-3">
              <button
                onClick={toggleConfig}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50 rounded-md transition-colors"
              >
                <span>Configuración</span>
                {configCollapsed ? (
                  <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                )}
              </button>
              {!configCollapsed && (
                <div className="mt-2 space-y-1">
                  <Link
                    to="/configuracion?tab=tienda-publica"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=tienda-publica')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🏢</span>
                    Tienda Pública
                  </Link>
                  <Link
                    to="/configuracion?tab=vitrina"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=vitrina')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🧭</span>
                    Vitrina Virtual
                  </Link>
                  <Link
                    to="/configuracion?tab=entrega"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=entrega')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🚚</span>
                    Entrega y Recogida
                  </Link>
                  <Link
                    to="/configuracion/envios/inter"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion/envios/inter')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📦</span>
                    Inter Rapidísimo (envíos)
                  </Link>
                  <Link
                    to="/configuracion/facturacion-electronica"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion/facturacion-electronica')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🧾</span>
                    Facturación electrónica
                  </Link>
                  <Link
                    to="/configuracion/qr-code"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion/qr-code')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                    Código QR Check-In
                  </Link>
                  <Link
                    to="/configuracion?tab=categorias"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=categorias')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🏷️</span>
                    Categorías
                  </Link>
                  <Link
                    to="/colecciones"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/colecciones')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🗂️</span>
                    Colecciones
                  </Link>
                  <Link
                    to="/configuracion?tab=ubicaciones"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=ubicaciones')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📍</span>
                    Ubicaciones
                  </Link>
                  <Link
                    to="/configuracion?tab=metodos-pago"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=metodos-pago')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">💳</span>
                    Métodos de Pago
                  </Link>
                  <Link
                    to="/configuracion?tab=formulario-web"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=formulario-web')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📄</span>
                    Formulario Web
                  </Link>
                  <Link
                    to="/configuracion?tab=calendario"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=calendario')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">📅</span>
                    Calendario
                  </Link>
                  <Link
                    to="/configuracion?tab=whatsapp"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=whatsapp')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">💬</span>
                    WhatsApp
                  </Link>
                  <Link
                    to="/configuracion?tab=plantillas-mensajes"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=plantillas-mensajes')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">💬</span>
                    Plantillas de Mensajes
                  </Link>
                  {canAccess('usuarios') && isVisible('usuarios') && (
                    <Link
                      to="/configuracion?tab=usuarios"
                      onClick={() => setOpen(false)}
                      className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                        ${isActive('/configuracion') && location.search.includes('tab=usuarios')
                          ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      <span className="mr-3 text-lg">👥</span>
                      Usuarios
                    </Link>
                  )}
                  <Link
                    to="/configuracion?tab=empresas"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=empresas')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🏢</span>
                    Empresas
                  </Link>
                  <Link
                    to="/configuracion?tab=branding"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=branding')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🎨</span>
                    Branding
                  </Link>
                  <Link
                    to="/configuracion?tab=notificaciones"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=notificaciones')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">🔔</span>
                    Notificaciones
                  </Link>
                  <Link
                    to="/configuracion?tab=sistema"
                    onClick={() => setOpen(false)}
                    className={`
                      group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                      ${isActive('/configuracion') && location.search.includes('tab=sistema')
                        ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    <span className="mr-3 text-lg">⚙️</span>
                    Sistema
                  </Link>
                  {isSuperAdminEmail(usuario?.email) && (
                    <>
                      <Link
                        to="/super-admin"
                        onClick={() => setOpen(false)}
                        className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-red-100 border-2 border-red-500
                        ${location.pathname === '/super-admin'
                          ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      >
                        <span className="mr-3 text-lg">🔧</span>
                        Super Admin
                      </Link>
                      <Link
                        to="/super-admin/referidos"
                        onClick={() => setOpen(false)}
                        className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                        ${location.pathname.startsWith('/super-admin/referidos')
                          ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      >
                        <span className="mr-3 text-lg">🤝</span>
                        Referidos
                      </Link>
                      <Link
                        to="/super-admin/acceso"
                        onClick={() => setOpen(false)}
                        className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                        ${location.pathname.startsWith('/super-admin/acceso')
                          ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      >
                        <span className="mr-3 text-lg">🎯</span>
                        Acceso anticipado CRM
                      </Link>
                      <Link
                        to="/super-admin/producto-global"
                        onClick={() => setOpen(false)}
                        className={`
                        group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200
                        ${location.pathname.startsWith('/super-admin/producto-global')
                          ? 'bg-blue-100 text-blue-900 border-r-2 border-blue-600'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      >
                        <span className="mr-3 text-lg">🌐</span>
                        Catálogo global (EAN)
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

        </div>

        {/* Footer del sidebar */}
        <div className="p-4 border-t border-gray-200 bg-white">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              Clientelo v1.0.0
            </p>
            <p className="text-xs text-gray-400 mt-1">
              CRM con BI para LATAM
            </p>
          </div>
        </div>
      </div>

      {/* Module Manager Panel — true overlay, outside sidebar */}
      <ModuleManagerPanel
        isOpen={moduleManagerOpen}
        onClose={() => setModuleManagerOpen(false)}
        visibilityMap={visibilityMap}
        onToggle={toggleModule}
      />
    </>
  );
};
